import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ZoomInIcon, XIcon } from 'lucide-react';
import { useBackNavigation } from '../hooks/useBackNavigation';
export function BioMaxing() {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const goBack = useBackNavigation('/work');
  return <div className="w-full min-h-screen bg-white" data-id="element-662">
      {/* Image Modal */}
      {selectedImage && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4" onClick={() => setSelectedImage(null)} data-id="element-663">
          <button onClick={() => setSelectedImage(null)} className="absolute top-4 right-4 text-white hover:text-cyan-400 transition-colors" data-id="element-664">
            <XIcon className="w-8 h-8" data-id="element-665" />
          </button>
          <motion.img initial={{
        scale: 0.9
      }} animate={{
        scale: 1
      }} src={selectedImage} alt="Enlarged view" className="max-w-full max-h-full object-contain" data-id="element-666" />
        </motion.div>}
      {/* Header */}
      <header className="py-4 px-6 bg-white border-b border-stone-200 sticky top-0 z-40" data-id="element-667">
        <div className="max-w-7xl mx-auto flex items-center justify-between" data-id="element-668">
          <button onClick={goBack} className="inline-flex items-center gap-2 text-stone-600 hover:text-stone-900 transition-colors" data-id="element-669">
            <ArrowLeftIcon className="w-5 h-5" data-id="element-670" />
            <span className="font-light" data-id="element-671">Back</span>
          </button>
          <h1 className="text-2xl font-light text-stone-900" data-id="element-672">Maria Elina</h1>
        </div>
      </header>
      {/* Hero Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-stone-50 via-cyan-50/30 to-stone-50" data-id="element-673">
        <div className="max-w-5xl mx-auto" data-id="element-674">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} data-id="element-675">
            <p className="text-cyan-600 font-light tracking-wide uppercase mb-4" data-id="element-676">
              Creative Direction, UX/UI Design
            </p>
            <h1 className="text-4xl md:text-6xl font-light text-stone-900 mb-6" data-id="element-677">
              BioMaxing
            </h1>
            <p className="text-xl text-stone-600 font-light mb-8" data-id="element-678">
              Biohacking multi-platform application spanning Web, Mobile and
              Telegram
            </p>
            <div className="space-y-4 text-stone-700 font-light" data-id="element-679">
              <p className="text-lg" data-id="element-680">
                <span className="font-normal text-stone-900" data-id="element-681">Role:</span>{' '}
                Co-Founder, Creative Director, Designer, Marketing Director
              </p>
              <p className="text-lg" data-id="element-682">
                <span className="font-normal text-stone-900" data-id="element-683">Platform:</span>{' '}
                Web, Mobile (iOS/Android), Telegram Bot
              </p>
              <p className="text-lg" data-id="element-684">
                <span className="font-normal text-stone-900" data-id="element-685">Status:</span> In
                Development
              </p>
              <p className="text-lg" data-id="element-686">
                <span className="font-normal text-stone-900" data-id="element-687">Year:</span> 2025
              </p>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Project Overview */}
      <section className="py-20 px-6 bg-white" data-id="element-688">
        <div className="max-w-5xl mx-auto" data-id="element-689">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-690">
            <h2 className="text-3xl font-light text-stone-900 mb-6" data-id="element-691">
              Project Overview
            </h2>
            <p className="text-lg text-stone-600 font-light leading-relaxed mb-6" data-id="element-692">
              BioMaxing is a startup we are currently actively developing with
              my technical co-founder. The project aims to create a
              comprehensive biohacking ecosystem that makes health optimization
              accessible and actionable for users at all levels of experience.
            </p>
            <p className="text-lg text-stone-600 font-light leading-relaxed mb-6" data-id="element-693">
              As Creative Director and UX/UI Designer, I am responsible for the
              entire visual identity, user experience strategy, and interface
              design across all platforms. My role also encompasses marketing
              direction, ensuring the brand resonates with our target audience
              of health-conscious individuals and biohacking enthusiasts.
            </p>
            <p className="text-lg text-stone-600 font-light leading-relaxed" data-id="element-694">
              The platform combines scientific research, community expertise,
              and advanced tracking tools to create personalized biohacking
              journeys. Users can access a comprehensive knowledge library,
              track their health metrics, connect with experts, and discover
              evidence-based practices for optimizing their well-being.
            </p>
          </motion.div>
        </div>
      </section>
      {/* Website Design - Desktop & Mobile */}
      <section className="py-20 px-6 bg-white" data-id="element-695">
        <div className="max-w-7xl mx-auto" data-id="element-696">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-697">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-698">
              Final Website Design
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-699">
              The refined website design balances futuristic aesthetics with
              accessibility, creating a seamless experience across all devices.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8 items-start" data-id="element-700">
              {/* Desktop - Takes 3 columns */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }} className="md:col-span-3 group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/homepage%204%20(1).png')} data-id="element-701">
                <div className="relative overflow-hidden rounded-2xl shadow-xl bg-stone-900" data-id="element-702">
                  <img src="https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/homepage%204%20(1).png" alt="BioMaxing Desktop Website" className="w-full h-auto object-contain" data-id="element-703" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-704">
                    <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-705" />
                  </div>
                </div>
                <div className="mt-4" data-id="element-706">
                  <h4 className="text-lg font-light text-stone-900 mb-2" data-id="element-707">
                    Desktop Experience
                  </h4>
                  <p className="text-stone-600 font-light text-sm" data-id="element-708">
                    Full website layout with hero section, feature highlights,
                    and clear navigation
                  </p>
                </div>
              </motion.div>
              {/* Mobile - Takes 1 column */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="md:col-span-1 group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/iPhone%20homepage.png')} data-id="element-709">
                <div className="relative overflow-hidden rounded-2xl shadow-xl bg-stone-900" data-id="element-710">
                  <img src="https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/iPhone%20homepage.png" alt="BioMaxing Mobile Website" className="w-full h-auto object-contain" data-id="element-711" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-712">
                    <ZoomInIcon className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-713" />
                  </div>
                </div>
                <div className="mt-4" data-id="element-714">
                  <h4 className="text-lg font-light text-stone-900 mb-2" data-id="element-715">
                    Mobile Experience
                  </h4>
                  <p className="text-stone-600 font-light text-sm" data-id="element-716">
                    Optimized responsive layout for on-the-go access
                  </p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Knowledge Library Planning */}
      <section className="py-20 px-6 bg-stone-50" data-id="element-717">
        <div className="max-w-7xl mx-auto" data-id="element-718">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-719">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-720">
              Knowledge Library Architecture
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-721">
              A comprehensive content architecture mapping the entire biohacking
              knowledge base—from foundational principles of human physiology to
              advanced optimization stacks and practices. This planning document
              guides the development of our educational content strategy.
            </p>
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/good.png')} data-id="element-722">
              <div className="relative overflow-hidden rounded-2xl shadow-xl" data-id="element-723">
                <img src="https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/good.png" alt="BioMaxing Knowledge Library Planning" className="w-full h-auto" data-id="element-724" />
                <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-725">
                  <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-726" />
                </div>
              </div>
              <p className="text-stone-600 font-light mt-4 text-center italic" data-id="element-727">
                Content structure planning for the comprehensive wellness and
                biohacking library
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>
      {/* Mobile App Design Development */}
      <section className="py-20 px-6 bg-stone-50" data-id="element-728">
        <div className="max-w-7xl mx-auto" data-id="element-729">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-730">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-731">
              Mobile App Design Process
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-732">
              The mobile application design process involves extensive iteration
              on user flows, interface components, and visualization patterns.
              This comprehensive design system ensures consistency across all
              mobile touchpoints while maintaining flexibility for future
              feature development.
            </p>
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/app%20wireframe.png')} data-id="element-733">
              <div className="relative overflow-hidden rounded-2xl shadow-xl" data-id="element-734">
                <img src="https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/app%20wireframe.png" alt="BioMaxing Mobile App Design Development" className="w-full h-auto" data-id="element-735" />
                <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-736">
                  <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-737" />
                </div>
              </div>
              <p className="text-stone-600 font-light mt-4 text-center italic" data-id="element-738">
                Comprehensive mobile app design system showing various screens
                and user flows
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>
      {/* Initial Website Concept */}
      <section className="py-20 px-6 bg-white" data-id="element-739">
        <div className="max-w-7xl mx-auto" data-id="element-740">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-741">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-742">
              Initial Website Concept
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-743">
              The website design evolved through multiple iterations, refining
              the visual language and user experience to create the polished
              interface we have today.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-744">
              {/* First Concept */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/Homepage_new.png')} data-id="element-745">
                <div className="relative overflow-hidden rounded-xl shadow-lg bg-stone-900 h-[600px]" data-id="element-746">
                  <img src="https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/Homepage_new.png" alt="BioMaxing Early Website Concept" className="w-full h-full object-cover object-top" data-id="element-747" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-748">
                    <ZoomInIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-749" />
                  </div>
                </div>
                <div className="mt-4" data-id="element-750">
                  <h4 className="text-lg font-light text-stone-900 mb-2" data-id="element-751">
                    Early Concept 1
                  </h4>
                  <p className="text-stone-600 font-light text-sm" data-id="element-752">
                    Initial exploration establishing the futuristic aesthetic
                  </p>
                </div>
              </motion.div>
              {/* Second Concept */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/home1.1.png')} data-id="element-753">
                <div className="relative overflow-hidden rounded-xl shadow-lg bg-stone-900 h-[600px]" data-id="element-754">
                  <img src="https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/home1.1.png" alt="BioMaxing Refined Website Concept" className="w-full h-full object-cover object-top" data-id="element-755" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-756">
                    <ZoomInIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-757" />
                  </div>
                </div>
                <div className="mt-4" data-id="element-758">
                  <h4 className="text-lg font-light text-stone-900 mb-2" data-id="element-759">
                    Early Concept 2
                  </h4>
                  <p className="text-stone-600 font-light text-sm" data-id="element-760">
                    Initial exploration focusing on familiar page structures
                  </p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Logo Design Process */}
      <section className="py-20 px-6 bg-stone-50" data-id="element-761">
        <div className="max-w-7xl mx-auto" data-id="element-762">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-763">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-764">
              Logo Design & Brand Identity
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-765">
              The logo system evolved through multiple iterations to create a
              flexible identity that works across all platforms while
              maintaining instant brand recognition.
            </p>
            {/* Design Iterations Grid */}
            <div className="mb-16" data-id="element-766">
              <h3 className="text-xl font-light text-stone-900 mb-8" data-id="element-767">
                Design Iterations
              </h3>
              {/* First Row - 3 Images */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6" data-id="element-768">
                {/* Initial Concepts */}
                <motion.div initial={{
                opacity: 0,
                y: 30
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.6
              }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/image%2081.png')} data-id="element-769">
                  <div className="relative overflow-hidden rounded-xl shadow-lg bg-stone-100" data-id="element-770">
                    <img src="https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/image%2081.png" alt="Initial logo concepts" className="w-full h-auto" data-id="element-771" />
                    <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-772">
                      <ZoomInIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-773" />
                    </div>
                  </div>
                  <p className="text-stone-600 font-light mt-3 text-sm text-center" data-id="element-774">
                    Initial concept explorations
                  </p>
                </motion.div>
                {/* Triangle Concepts */}
                <motion.div initial={{
                opacity: 0,
                y: 30
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.6,
                delay: 0.1
              }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/triangle.png')} data-id="element-775">
                  <div className="relative overflow-hidden rounded-xl shadow-lg" data-id="element-776">
                    <img src="https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/triangle.png" alt="Triangle-based concepts" className="w-full h-auto" data-id="element-777" />
                    <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-778">
                      <ZoomInIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-779" />
                    </div>
                  </div>
                  <p className="text-stone-600 font-light mt-3 text-sm text-center" data-id="element-780">
                    Triangle-based explorations
                  </p>
                </motion.div>
                {/* Angled Concepts */}
                <motion.div initial={{
                opacity: 0,
                y: 30
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.6,
                delay: 0.2
              }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/angled.png')} data-id="element-781">
                  <div className="relative overflow-hidden rounded-xl shadow-lg" data-id="element-782">
                    <img src="https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/angled.png" alt="Angled design variations" className="w-full h-auto" data-id="element-783" />
                    <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-784">
                      <ZoomInIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-785" />
                    </div>
                  </div>
                  <p className="text-stone-600 font-light mt-3 text-sm text-center" data-id="element-786">
                    Angled design variations
                  </p>
                </motion.div>
              </div>
              {/* Second Row - 1 Image */}
              <div className="mb-6" data-id="element-787">
                <motion.div initial={{
                opacity: 0,
                y: 30
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.6,
                delay: 0.3
              }} className="group cursor-pointer max-w-md mx-auto" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/minimalist.png')} data-id="element-788">
                  <div className="relative overflow-hidden rounded-xl shadow-lg" data-id="element-789">
                    <img src="https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/minimalist.png" alt="Minimalist approach" className="w-full h-auto" data-id="element-790" />
                    <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-791">
                      <ZoomInIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-792" />
                    </div>
                  </div>
                  <p className="text-stone-600 font-light mt-3 text-sm text-center" data-id="element-793">
                    Minimalist approach
                  </p>
                </motion.div>
              </div>
              {/* Third Row - 3 Images */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-794">
                {/* Hand Symbol Development */}
                <motion.div initial={{
                opacity: 0,
                y: 30
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.6,
                delay: 0.4
              }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/image_2025-11-06_21-46-47.png')} data-id="element-795">
                  <div className="relative overflow-hidden rounded-xl shadow-lg h-64" data-id="element-796">
                    <img src="https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/image_2025-11-06_21-46-47.png" alt="Hand symbol development" className="w-full h-full object-cover object-top" data-id="element-797" />
                    <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-798">
                      <ZoomInIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-799" />
                    </div>
                  </div>
                  <p className="text-stone-600 font-light mt-3 text-sm text-center" data-id="element-800">
                    Hand symbol development
                  </p>
                </motion.div>
                {/* Graphic Exploration */}
                <motion.div initial={{
                opacity: 0,
                y: 30
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.6,
                delay: 0.5
              }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/image_2025-11-06_21-47-12.png')} data-id="element-801">
                  <div className="relative overflow-hidden rounded-xl shadow-lg h-64" data-id="element-802">
                    <img src="https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/image_2025-11-06_21-47-12.png" alt="Graphic exploration" className="w-full h-full object-cover object-top" data-id="element-803" />
                    <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-804">
                      <ZoomInIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-805" />
                    </div>
                  </div>
                  <p className="text-stone-600 font-light mt-3 text-sm text-center" data-id="element-806">
                    Graphic exploration
                  </p>
                </motion.div>
                {/* Refined Hand Concept */}
                <motion.div initial={{
                opacity: 0,
                y: 30
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.6,
                delay: 0.6
              }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/wristless.png')} data-id="element-807">
                  <div className="relative overflow-hidden rounded-xl shadow-lg h-64" data-id="element-808">
                    <img src="https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/wristless.png" alt="Refined hand concept" className="w-full h-full object-cover object-top" data-id="element-809" />
                    <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-810">
                      <ZoomInIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-811" />
                    </div>
                  </div>
                  <p className="text-stone-600 font-light mt-3 text-sm text-center" data-id="element-812">
                    Refined hand concept
                  </p>
                </motion.div>
              </div>
            </div>
            {/* Final Selected System */}
            <div data-id="element-813">
              <h3 className="text-xl font-light text-stone-900 mb-6" data-id="element-814">
                Final Logo System
              </h3>
              <p className="text-stone-600 font-light mb-8 max-w-3xl" data-id="element-815">
                The hand symbol emerged as the core visual anchor, with a
                modular system allowing context-specific variations for
                different platforms while maintaining instant brand recognition.
              </p>
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/final_3.png')} data-id="element-816">
                <div className="relative overflow-hidden rounded-2xl shadow-xl" data-id="element-817">
                  <img src="https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/final_3.png" alt="BioMaxing Final Logo System" className="w-full h-auto" data-id="element-818" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-819">
                    <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-820" />
                  </div>
                </div>
                <p className="text-stone-600 font-light mt-4 text-center italic" data-id="element-821">
                  Multi-channel logo system with variations for different
                  touchpoints
                </p>
              </motion.div>
            </div>
            {/* Social Media Previews */}
            <div className="mt-16" data-id="element-822">
              <h3 className="text-xl font-light text-stone-900 mb-6" data-id="element-823">
                Social Media Applications
              </h3>
              <p className="text-stone-600 font-light mb-8 max-w-3xl" data-id="element-824">
                The brand identity extends to social media platforms with
                attractive previews and tailored visuals that maintain
                consistency while adapting to different formats and messaging
                needs.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-id="element-825">
                <motion.div initial={{
                opacity: 0,
                y: 30
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.6
              }} className="md:col-span-3 group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/Ecosystem%20of%20your%20well-being.png')} data-id="element-826">
                  <div className="relative overflow-hidden rounded-xl shadow-lg" data-id="element-827">
                    <img src="https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/Ecosystem%20of%20your%20well-being.png" alt="Social media preview - Ecosystem messaging" className="w-full h-auto" data-id="element-828" />
                    <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-829">
                      <ZoomInIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-830" />
                    </div>
                  </div>
                </motion.div>
                <motion.div initial={{
                opacity: 0,
                y: 30
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.6,
                delay: 0.1
              }} className="md:col-span-1 group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/Your%20paragraph%20text%20(4).png')} data-id="element-831">
                  <div className="relative overflow-hidden rounded-xl shadow-lg" data-id="element-832">
                    <img src="https://storage.yandexcloud.net/maria-images/images/biomaxing_ready/Your%20paragraph%20text%20(4).png" alt="Social media preview - Brand messaging" className="w-full h-auto" data-id="element-833" />
                    <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-834">
                      <ZoomInIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-835" />
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Design Approach */}
      <section className="py-20 px-6 bg-stone-50" data-id="element-836">
        <div className="max-w-5xl mx-auto" data-id="element-837">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-838">
            <h2 className="text-3xl font-light text-stone-900 mb-8" data-id="element-839">
              Design Approach
            </h2>
            <div className="space-y-6 text-lg text-stone-600 font-light leading-relaxed" data-id="element-840">
              <p data-id="element-841">
                The design strategy for BioMaxing centers on making complex
                scientific concepts accessible without sacrificing depth. Every
                interface decision is informed by the goal of creating a
                seamless and consistent brand experience across web, mobile, and
                messaging platforms.
              </p>
              <p data-id="element-842">
                The visual language combines futuristic aesthetics with
                approachable, human-centered design. The color palette -
                anchored by cyan and teal tones - evokes both technology and
                wellness, while the typography and spacing create a sense of
                clarity and breathing room essential for processing health
                information.
              </p>
              <p data-id="element-843">
                As the project continues to develop, the design system evolves
                based on user feedback and technical constraints, always
                maintaining the core principle: empowering users to take control
                of their health through informed, personalized optimization
                strategies.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
}